import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSpace } from "@/lib/space";

const W = 320;
const GAP = 46;
const H = GAP * 12 + 80;
const pt = (k) => ({ x: W / 2 + Math.sin(k * 0.95) * 92, y: H - 40 - k * GAP });

// The 13 gates as a rising, glowing path: Root at the base, Embodied Enlightenment at the crown.
export default function GateMap({ gates }) {
  const navigate = useNavigate();
  const space = useSpace();
  const pts = gates.map((_, k) => pt(k));
  const current = gates.find((x) => x.published && !x.locked && !x.done)?.g;
  const d = pts.map((p, k) => (k ? `S ${(pts[k - 1].x + p.x) / 2 + (k % 2 ? 40 : -40)} ${(pts[k - 1].y + p.y) / 2} ${p.x} ${p.y}` : `M ${p.x} ${p.y}`)).join(" ");
  const doneCount = gates.filter((x) => x.done).length;
  return (
    <div data-testid="qi-gate-map" className="relative mt-8 border border-[#D9B47A]/15 bg-[radial-gradient(ellipse_at_center,rgba(194,65,79,0.12),transparent_70%)]">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`Gate map, ${doneCount} of 13 complete`}>
        <defs>
          <filter id="qi-glow" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="4" /></filter>
        </defs>
        <path d={d} fill="none" stroke="#D9B47A" strokeOpacity="0.18" strokeWidth="1.5" strokeDasharray="3 5" />
        {gates.map((x, k) => {
          const p = pts[k];
          const isCur = x.g === current;
          const dim = !x.published || x.locked;
          const left = p.x > W / 2;
          return (
            <g key={x.g} data-testid={`qi-map-gate-${x.g}`} data-state={x.done ? "done" : isCur ? "current" : dim ? "closed" : "open"} onClick={() => navigate(space.gate(x.g))} className="cursor-pointer" role="link" aria-label={`Gate ${x.g + 1} ${x.name}`}>
              {(x.done || isCur) && <circle cx={p.x} cy={p.y} r={x.done ? 14 : 16} fill={x.done ? "#C2414F" : "#D9B47A"} opacity="0.55" filter="url(#qi-glow)" />}
              {isCur && (
                <motion.circle cx={p.x} cy={p.y} r="15" fill="none" stroke="#D9B47A" strokeWidth="1" initial={{ opacity: 0.8, scale: 1 }} animate={{ opacity: [0.8, 0, 0.8], scale: [1, 1.7, 1] }} transition={{ duration: 2.6, repeat: Infinity }} style={{ transformOrigin: `${p.x}px ${p.y}px` }} />
              )}
              <circle cx={p.x} cy={p.y} r="11" fill={x.done ? "#C2414F" : "#0B0509"} stroke={x.done ? "#E8838E" : "#D9B47A"} strokeOpacity={dim && !x.done ? 0.35 : 1} strokeWidth="1.2" />
              <text x={p.x} y={p.y + 3.5} textAnchor="middle" fontSize="9" fontFamily="monospace" fill={x.done ? "#F2E8DF" : dim ? "#8F8176" : "#D9B47A"}>{String(x.g + 1).padStart(2, "0")}</text>
              <text x={left ? p.x - 20 : p.x + 20} y={p.y + 4} textAnchor={left ? "end" : "start"} fontSize="12.5" fontFamily="Georgia, serif" fill={dim && !x.done ? "#8F8176" : "#F2E8DF"}>{x.name}</text>
            </g>
          );
        })}
      </svg>
      <p className="absolute top-4 left-4 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Crown</p>
      <p className="absolute bottom-4 left-4 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Root</p>
      <p data-testid="qi-map-count" className="absolute top-4 right-4 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">{doneCount}/13 lit</p>
    </div>
  );
}
